import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError, providerApi, type ProviderApprovalStatus, type ProviderExtensionRequest,
  type ProviderMembershipRole, type UserRole,
} from "../../lib/api";
import { subscribeRealtimeEvent } from "../../lib/realtime";

export interface ExtensionApprovalAuthority {
  userId: string | null;
  userRole: UserRole | null;
  membershipRole: ProviderMembershipRole | null;
  providerStatus: ProviderApprovalStatus | null;
}

export function canReviewExtensionRequests(authority: ExtensionApprovalAuthority) {
  return Boolean(authority.userId &&
    (authority.userRole === "provider_owner" || authority.userRole === "provider_staff") &&
    (authority.membershipRole === "owner" || authority.membershipRole === "manager") &&
    authority.providerStatus === "approved");
}

export function extensionDeadline(request: ProviderExtensionRequest) {
  // Never start a new two-minute timer from requested_at, a refresh, or a click.
  return Math.min(Date.parse(request.quote.approval_deadline ?? request.quote.expires_at), Date.parse(request.quote.expires_at));
}

export function isReviewableExtension(request: ProviderExtensionRequest, now = Date.now()) {
  const { quote, session } = request;
  const sameEnd = quote.current_end_at === null ? session.planned_end_at === null
    : session.planned_end_at !== null && Date.parse(quote.current_end_at) === Date.parse(session.planned_end_at);
  return quote.contract_version === 2 && quote.phase === "pending_approval" && quote.decision === null &&
    quote.applied_at === null && session.status === "active" && sameEnd &&
    quote.expected_notification_revision === session.notification_revision &&
    Number.isFinite(extensionDeadline(request)) && extensionDeadline(request) > now &&
    Number.isFinite(Date.parse(quote.proposed_end_at)) &&
    [quote.current_total_amount, quote.additional_amount, quote.proposed_total_amount].every((amount) => Number.isFinite(amount) && amount >= 0) &&
    Number.isInteger(quote.additional_minutes) && quote.additional_minutes >= 15 && quote.additional_minutes <= 240 &&
    quote.additional_minutes % 15 === 0 && typeof quote.currency === "string" && Boolean(quote.currency.trim());
}

function scoped(request: ProviderExtensionRequest, venueId: string, quoteId?: string, sessionId?: string) {
  const quote = request?.quote;
  const phaseMatches = quote?.phase === "pending_approval" ? quote.status === "pending" && quote.decision === null && quote.applied_at === null
    : quote?.phase === "applied" ? quote.status === "applied" && quote.decision === "approved" && Boolean(quote.applied_at && Number.isFinite(Date.parse(quote.applied_at)))
      : quote?.phase === "expired" ? quote.status === "expired" && quote.applied_at === null
        : quote && ["rejected", "cancelled", "stale"].includes(quote.phase) && quote.status === "rejected" && quote.applied_at === null && quote.decision === quote.phase;
  return Boolean(request?.quote?.id && request?.session?.id && request.session.venue_id === venueId &&
    request.quote.session_id === request.session.id && request.quote.contract_version === 2 && request.quote.requested_at &&
    phaseMatches && (!quoteId || request.quote.id === quoteId) && (!sessionId || request.session.id === sessionId));
}

function errorKey(error: unknown) {
  if (!(error instanceof ApiError)) return "extensions.error.unconfirmed";
  if (error.status === 401 || error.status === 403) return "extensions.error.denied";
  if (error.status === 404) return "extensions.error.unavailable";
  if (error.detail === "extension_request_expired" || error.detail === "extension_quote_expired") return "extensions.error.expired";
  if (error.detail === "extension_quote_price_changed") return "extensions.error.priceChanged";
  if (error.detail === "extension_booking_conflict" || error.detail === "extension_blackout_conflict") return "extensions.error.conflict";
  if (error.detail === "extension_session_ended" || error.detail === "extension_session_unavailable" || error.detail === "extension_end_in_past") return "extensions.error.sessionChanged";
  if (error.status === 409) return "extensions.error.stale";
  return "extensions.error.unconfirmed";
}

type Operation = { key: string; quoteId: string | null; action: "approve" | "reject" | "refresh" };
interface Snapshot {
  key: string | null;
  requests: ProviderExtensionRequest[];
  lastResult: ProviderExtensionRequest | null;
  ready: boolean;
  refreshing: boolean;
  busy: Operation | null;
  needsRefresh: string[];
  denied: boolean;
  error: string | null;
  notice: string | null;
}
const empty = (key: string | null): Snapshot => ({ key, requests: [], lastResult: null, ready: false, refreshing: false, busy: null, needsRefresh: [], denied: false, error: null, notice: null });

export function usePendingExtensionRequests({
  venueId, authority, isOnline, onSessionChanged,
}: {
  venueId: string | null;
  authority: ExtensionApprovalAuthority;
  isOnline: boolean;
  onSessionChanged?: (request: ProviderExtensionRequest) => Promise<void>;
}) {
  const allowed = canReviewExtensionRequests(authority);
  const key = allowed && venueId ? `${authority.userId}:${authority.userRole}:${authority.membershipRole}:${venueId}` : null;
  const [snapshot, setSnapshot] = useState<Snapshot>(() => empty(key));
  const state = useRef(snapshot);
  state.current = snapshot;
  const keyRef = useRef(key);
  keyRef.current = key;
  const onlineRef = useRef(isOnline);
  onlineRef.current = isOnline;
  const sessionChanged = useRef(onSessionChanged);
  sessionChanged.current = onSessionChanged;
  const mounted = useRef(true);
  const controllers = useRef(new Set<AbortController>());
  const operation = useRef<Operation | null>(null);
  const isCurrent = (scope: string, task?: Operation) => mounted.current && keyRef.current === scope && (!task || operation.current === task);
  const online = () => onlineRef.current && (typeof navigator === "undefined" || navigator.onLine);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controllers.current.forEach((controller) => controller.abort()); };
  }, []);

  const call = useCallback(async <T,>(request: (signal: AbortSignal) => Promise<T>) => {
    const controller = new AbortController();
    controllers.current.add(controller);
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    try { return await request(controller.signal); }
    finally { window.clearTimeout(timeout); controllers.current.delete(controller); }
  }, []);

  function acceptResult(result: ProviderExtensionRequest, scope: string) {
    if (!isCurrent(scope)) return;
    setSnapshot((current) => {
      if (current.key !== scope) return current;
      // A terminal decision cannot become pending again on the same quote ID.
      if (current.lastResult?.quote.id === result.quote.id && result.quote.phase === "pending_approval") return current;
      const rest = current.requests.filter((request) => request.quote.id !== result.quote.id);
      const pending = result.quote.phase === "pending_approval";
      return {
        ...current, ready: true, denied: false,
        requests: pending ? [...rest, result] : rest,
        lastResult: pending ? current.lastResult : result,
        needsRefresh: current.needsRefresh.filter((id) => id !== result.quote.id),
        notice: result.quote.phase === "applied" ? "extensions.notice.applied"
          : result.quote.phase === "rejected" ? "extensions.notice.rejected" : null,
      };
    });
  }

  function deny(scope: string) {
    if (isCurrent(scope)) setSnapshot({ ...empty(scope), ready: true, denied: true, error: "extensions.error.denied" });
  }

  async function updateFloor(result: ProviderExtensionRequest, scope: string, task: Operation) {
    if (result.quote.phase !== "applied" || !isCurrent(scope, task)) return;
    if (!online()) {
      setSnapshot((current) => ({ ...current, error: "extensions.error.floorRefresh" }));
      return;
    }
    try { await sessionChanged.current?.(result); }
    catch { if (isCurrent(scope, task)) setSnapshot((current) => ({ ...current, error: "extensions.error.floorRefresh" })); }
  }

  async function readRequest(quoteId: string, scope: string, task: Operation, expectedSessionId?: string) {
    if (!online()) throw new ApiError(0, "Offline");
    const result = await call((signal) => providerApi.getExtensionRequest(quoteId, signal));
    if (!isCurrent(scope, task)) return null;
    if (!venueId || !scoped(result, venueId, quoteId, expectedSessionId)) throw new Error("Mismatched extension request.");
    acceptResult(result, scope);
    await updateFloor(result, scope, task);
    return isCurrent(scope, task) ? result : null;
  }

  const refresh = useCallback(async () => {
    if (!key || !venueId || !online() || operation.current?.key === key) return;
    const task: Operation = { key, quoteId: null, action: "refresh" };
    operation.current = task;
    setSnapshot((current) => ({ ...(current.key === key ? current : empty(key)), refreshing: true, error: null }));
    try {
      const old = state.current.key === key ? state.current : empty(key);
      // Resolve uncertain writes by the original request identity before a list
      // (an applied request no longer appears in the pending-only list).
      for (const quoteId of old.needsRefresh) {
        const known = old.requests.find((request) => request.quote.id === quoteId);
        await readRequest(quoteId, key, task, known?.session.id);
      }
      if (old.error === "extensions.error.floorRefresh" && old.lastResult && !old.needsRefresh.includes(old.lastResult.quote.id)) {
        await readRequest(old.lastResult.quote.id, key, task, old.lastResult.session.id);
      }
      if (!isCurrent(key, task)) return;
      const result = await call((signal) => providerApi.listExtensionRequests(signal));
      if (!isCurrent(key, task)) return;
      if (!Array.isArray(result.requests)) throw new Error("Invalid extension list.");
      const requests = result.requests.filter((request) => scoped(request, venueId) && request.quote.phase === "pending_approval");
      setSnapshot((current) => current.key === key ? { ...current,
        requests: requests.filter((request) => request.quote.id !== current.lastResult?.quote.id), ready: true, denied: false,
        error: current.error === "extensions.error.floorRefresh" ? current.error : null } : current);
    } catch (error) {
      if (!isCurrent(key, task)) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) deny(key);
      else setSnapshot((current) => ({ ...current, ready: true, error: current.needsRefresh.length ? "extensions.error.recovery" : "extensions.error.load" }));
    } finally {
      const current = isCurrent(key, task);
      if (operation.current === task) operation.current = null;
      if (current) setSnapshot((value) => ({ ...value, refreshing: false }));
    }
  }, [key, venueId, call]);

  useEffect(() => {
    operation.current = null;
    setSnapshot(empty(key));
    if (key && isOnline) void refresh();
    return () => { controllers.current.forEach((controller) => controller.abort()); };
  }, [key]);

  useEffect(() => {
    if (!key || !isOnline) return;
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 15_000);
    const unsubscribe = subscribeRealtimeEvent((event) => {
      const payload = event.payload as Record<string, unknown> | null;
      if (event.type === "resync" || event.type === "queue.overflow" ||
          ((event.type === "session.extension_requested.v1" || event.type === "session.updated" || event.type === "session.stopped") && payload?.venue_id === venueId)) {
        void refresh();
      }
    });
    return () => { window.clearInterval(timer); unsubscribe(); };
  }, [key, isOnline, venueId, refresh]);

  async function refreshRequest(quoteId: string) {
    if (!key || !online() || operation.current?.key === key) return;
    const known = state.current.key === key ? state.current.requests.find((request) => request.quote.id === quoteId) ?? state.current.lastResult : null;
    if (!known || known.quote.id !== quoteId) return;
    const task: Operation = { key, quoteId, action: "refresh" };
    operation.current = task;
    setSnapshot((current) => ({ ...current, busy: task, error: null }));
    try { await readRequest(quoteId, key, task, known.session.id); }
    catch (error) {
      if (!isCurrent(key, task)) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) deny(key);
      else setSnapshot((current) => ({ ...current, error: "extensions.error.recovery", needsRefresh: [...new Set([...current.needsRefresh, quoteId])] }));
    } finally {
      const current = isCurrent(key, task);
      if (operation.current === task) operation.current = null;
      if (current) setSnapshot((value) => ({ ...value, busy: null }));
    }
  }

  async function decide(quoteId: string, action: "approve" | "reject") {
    if (!key || !venueId || !online() || operation.current?.key === key || state.current.key !== key || state.current.denied) return;
    const known = state.current.requests.find((request) => request.quote.id === quoteId);
    if (!known || state.current.needsRefresh.includes(quoteId) || !isReviewableExtension(known)) return;
    const task: Operation = { key, quoteId, action };
    operation.current = task;
    setSnapshot((current) => ({ ...current, busy: task, error: null, notice: null }));
    try {
      const result = await call((signal) => action === "approve"
        ? providerApi.approveExtensionRequest(quoteId, signal) : providerApi.rejectExtensionRequest(quoteId, signal));
      if (!isCurrent(key, task)) return;
      if (!scoped(result, venueId, quoteId, known.session.id) ||
          (action === "approve" ? result.quote.phase !== "applied" || result.quote.decision !== "approved" || !result.quote.applied_at
            : result.quote.phase !== "rejected" || result.quote.decision !== "rejected")) throw new Error("Decision not confirmed.");
      acceptResult(result, key);
      await updateFloor(result, key, task);
    } catch (error) {
      if (!isCurrent(key, task)) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) { deny(key); return; }
      setSnapshot((current) => ({ ...current, error: errorKey(error), needsRefresh: [...new Set([...current.needsRefresh, quoteId])] }));
      // A response can be lost after commit. GET includes the current session;
      // never automatically repeat POST or call the manual extension endpoint.
      try {
        const current = await readRequest(quoteId, key, task, known.session.id);
        if (current && !(error instanceof ApiError && error.status === 409)) {
          setSnapshot((value) => ({ ...value, error: value.error === "extensions.error.floorRefresh" ? value.error : current.quote.phase === "applied" || current.quote.phase === "rejected"
            ? null : current.quote.phase === "expired" ? "extensions.error.expired"
              : current.quote.phase === "cancelled" ? "extensions.error.cancelled"
                : current.quote.phase === "stale" ? "extensions.error.stale" : "extensions.error.pendingRetry" }));
        }
      } catch (recoveryError) {
        if (!isCurrent(key, task)) return;
        if (recoveryError instanceof ApiError && (recoveryError.status === 401 || recoveryError.status === 403)) deny(key);
        else setSnapshot((current) => ({ ...current, error: "extensions.error.recovery" }));
      }
    } finally {
      const current = isCurrent(key, task);
      if (operation.current === task) operation.current = null;
      if (current) setSnapshot((value) => ({ ...value, busy: null }));
    }
  }

  const current = snapshot.key === key ? snapshot : empty(key);
  return {
    ...current, allowed, refresh, refreshRequest, decide,
    requests: key ? current.requests.filter((request) => venueId && scoped(request, venueId)) : [],
    lastResult: key && venueId && current.lastResult && scoped(current.lastResult, venueId) ? current.lastResult : null,
  };
}
